import { html, useEffect, useState } from '/vendor/htm-preact.module.js';

/**
 * Shot and match clocks.
 *
 * A countdown is a subtraction from a timestamp, not a stream of ticks. The
 * match clock counts from when the match was opened; the shot clock counts
 * from the last thing that happened in it. Both are worked out here from the
 * numbers the server already sends, so two screens watching the same match
 * agree without anything being broadcast between them.
 *
 * Running out SIGNALS and does not act. Recording a foul nobody adjudicated
 * would put a fact in a log that has no delete — only a compensation somebody
 * would have to notice was needed. The clock says the time is up; whoever is
 * scoring decides whether it was a foul, the same as any other foul.
 */
export const CLOCKS = [
  { id: 'shot', label: 'Shot clock', unit: 's', key: 'cueforge.clock.shot',
    options: [0, 30, 45, 60],
    say: (v) => (v ? `${v} seconds a shot` : 'No shot clock') },
  { id: 'match', label: 'Match clock', unit: 'm', key: 'cueforge.clock.match',
    options: [0, 30, 45, 60, 90],
    say: (v) => (v ? `${v} minutes for the match` : 'No match clock') },
];

/** A remembered choice — and never a crash when storage is blocked. */
export function recall(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : Number(v);
  } catch (e) { return fallback; }
}

export function remember(key, value) {
  try { localStorage.setItem(key, String(value)); } catch (e) { /* fine */ }
}

/** m:ss, or 0:00 once it has run out. Never negative — nobody counts up. */
export function clockText(seconds) {
  const left = Math.max(0, Math.ceil(seconds));
  return `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`;
}

/**
 * Seconds left, ticking.
 *
 * `from` is when the count started, as an ISO string; changing it restarts the
 * clock, which is how the shot clock resets on every recorded shot without
 * anybody telling it to.
 *
 * The match clock passes a timestamp the SERVER sent, so every screen watching
 * agrees. The shot clock currently passes one the browser made — see the TODO
 * in live.js for why that is fine for one scorer and drifts for two.
 */
export function useCountdown(seconds, from) {
  const started = from ? Date.parse(from) : null;
  const left = () => (seconds && started
    ? seconds - (Date.now() - started) / 1000 : null);
  const [remaining, setRemaining] = useState(left);

  useEffect(() => {
    if (!seconds || !started) { setRemaining(null); return undefined; }
    setRemaining(left());
    // Four times a second: enough that the last second does not appear to
    // stick, cheap enough to leave running on a wall for an evening.
    const tick = setInterval(() => setRemaining(left()), 250);
    return () => clearInterval(tick);
  }, [seconds, from]);

  return remaining;
}

/** One clock, with the last ten seconds and zero saying so. */
export function Clock({ label, seconds, from, testid }) {
  const left = useCountdown(seconds, from);
  if (left === null) return null;
  const out = left <= 0;
  const soon = !out && left <= 10;
  return html`
    <div class=${`clock ${out ? 'out' : ''} ${soon ? 'soon' : ''}`}
         data-testid=${testid}>
      <span class="clocklabel">${label}</span>
      <span class="clockleft" data-testid=${`${testid}-left`}>
        ${clockText(left)}
      </span>
      ${out && html`
        <span class="clocksays" data-testid=${`${testid}-out`}>
          Time — call it if it was
        </span>`}
    </div>`;
}
